package notifications

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.NotificationState
import apiclient.geoobjects.NotificationTags
import apiclient.geoobjects.TranslationTags
import apiclient.geoobjects.isFlagged
import apiclient.localizations.LocalizationArg
import apiclient.localizations.TranslationKey
import apiclient.tags.getUniqueTag
import apiclient.validations.parseEnumValue
import data.ObjectAndUserHandler
import dev.fritz2.components.flexBox
import dev.fritz2.components.stackUp
import dev.fritz2.core.RenderContext
import dev.fritz2.core.placeholder
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import localization.TL
import localization.Translation
import localization.getString
import localization.getTranslationFlow
import localization.translate
import mainmenu.ActiveSearchMenuStore
import model.NotificationNumbers
import search.getSvgIconOptions
import search.separationLine
import search.subtitleBox
import search.subtitleInfoText
import search.titleBox
import search.titleSizedText
import search.titleSubtitleStack
import svgmarker.MarkerSize
import svgmarker.renderSvgIcon
import theme.FormationUIIcons
import twcomponents.doOnceWhenElementInDOM
import twcomponents.twColOf
import twcomponents.twColOfCenter
import twcomponents.twGhostButton
import twcomponents.twInputField
import twcomponents.twInputTextField
import twcomponents.twLargeIconButtonNeutralRounded
import twcomponents.twMainTitle
import twcomponents.twRowOf
import twcomponents.twRowOfJustifyBetween
import twcomponents.twTooltip
import utils.ToggleStore
import utils.formatDateTime
import utils.getShortMessage
import utils.parseInstant


class NotificationToggleStore : ToggleStore()

//const val notificationContainerId = "notification-container"

fun RenderContext.notificationComponent() {

    withKoin {
        val objectAndUserHandler: ObjectAndUserHandler = get()
        val notificationToggleStore: NotificationToggleStore = get()
        val activeSearchMenuStore: ActiveSearchMenuStore = get()
        val notificationSearchInputFieldStore: NotificationSearchInputFieldStore by koinCtx.inject()
        val globalNotificationResultsStore: GlobalNotificationResultsStore by koinCtx.inject()

//        notificationToggleStore.data.render { notificationEnabled ->
//            if (notificationEnabled) {
//                // Main notification component container
//                div(
//                    baseClass = "flex flex-col gap-1 overflow-hidden w-full md:w-120 md:min-w-100 h-min min-h-min max-h-128 z-[1020] md:z-[1009] absolute top-0 md:top-4 md:relative rounded-b-2xl md:rounded-2xl bg-formationWhite transition-all duration-300 ease-in-out shadow-2xl", //md:left-4
//                    id = notificationContainerId,
//                ) {
        twColOf("w-full px-2 pt-4 pb-3") {
            //Row of Title and Close button
            twRowOfJustifyBetween {
                twMainTitle {
                    translate(TL.MainMenu.NOTIFICATION_CENTER)
                }
                // Button to close notifications
                twLargeIconButtonNeutralRounded(icon = FormationUIIcons.Close) {
                    clicks handledBy {
                        notificationToggleStore.update(false)
                        activeSearchMenuStore.reset(Unit)
                    }
                }.twTooltip(positioning = PlacementValues.left, fallback = TL.Notifications.CLOSE_NOTIFICATIONS.getString()) {
                    TL.Notifications.CLOSE_NOTIFICATIONS.getTranslationFlow().renderText()
                }
            }

            twRowOf {
                twInputField(notificationSearchInputFieldStore) {
                    twInputTextField {
                        doOnceWhenElementInDOM(domNode = domNode) {
                            domNode.focus()
                        }
                        placeholder(TL.Search.FIND_IT_FAST.getTranslationFlow())
                    }
                }
                // Mark as Read button
                globalNotificationResultsStore.data.map { it.unreadNumber }.render { unreadNumber ->
                    if (unreadNumber > 0) twGhostButton(
                        text = TL.Notifications.MARK_ALL_NEW_AS_READ,
                    ) {
                        clicks handledBy globalNotificationResultsStore.markAllAsRead
                    }
                }
            }
        }

        // Combined search results with buttons to select navigation point source options: LastKnownLocation, MapCenter, Your Location (GPS)
        globalNotificationResultsStore.data.render { (reads, _, unreads, _) ->
            if (reads.isNotEmpty() || unreads.isNotEmpty()) {

                div(
                    "flex flex-col w-full max-h-100 mt-1 rounded-b-2xl overflow-y-auto", // md:max-w-100 bg-white border border-gray-300 shadow-lg
                ) {
                    unreads.forEach { obj ->
                        twRowOfJustifyBetween {
                            div("w-full p-1 cursor-pointer hover:bg-gray-100") {
                                newNotificationListEntry(obj)
                                clicks handledBy {
                                    objectAndUserHandler.updateAndLocateObjectOrUserFromList(obj)
                                    notificationSearchInputFieldStore.update("")
                                }
                            }
                        }
                    }
                    separationLine(
                        title = TL.Notifications.OLDER_ALERTS.getTranslationFlow(),
                    )
                    reads.forEach { obj ->
                        div("p-1 cursor-pointer hover:bg-gray-100") {
                            newNotificationListEntry(obj)
                            clicks handledBy {
                                objectAndUserHandler.updateAndLocateObjectOrUserFromList(obj)
                                notificationSearchInputFieldStore.update("")
                            }
                        }
                    }
                }
            } else {
                twColOfCenter {
                    span {
                        translate(TL.Search.NO_NOTIFICATIONS)
                    }
                }
            }
        }
//                }
//            }
//        }
    }
}

fun RenderContext.newNotificationListEntry(
    notificationGeoObject: GeoObjectDetails,
) {
    val hasConnectedObject = !notificationGeoObject.connectedTo.isNullOrEmpty()
    val connectedObjectTitle = notificationGeoObject.tags.getUniqueTag(LocalizationArg.OBJECT_TITLE)

    if (hasConnectedObject) {
        div("flex flex-row w-full gap-1 pl-1 pr-2 py-1 items-center justify-start rounded-xl overflow-hidden") {
            // iconBox
            div("flex w-11 h-11 grow-0 shrink-0 basis-11 items-center justify-center mr-1") {
                getSvgIconOptions(
                    obj = notificationGeoObject,
                    flagged = notificationGeoObject.isFlagged,
                    showStateIndicator = true,
                )?.copy(size = MarkerSize.S)?.let { renderSvgIcon(it) }
            }
            titleSubtitleStack {
                titleBox(
                    titleText = {
                        connectedObjectTitle?.let { title ->
                            titleSizedText {
                                +title
                            }
                        }

                    },
                    titleInfo = {
                        span("text-xs text-right") {
                            notificationGeoObject.createdAt.parseInstant()?.let { createdAtTime ->
                                +createdAtTime.formatDateTime()
                            }
                        }
                    },
                )
                subtitleBox(
                    subtitleText = {
                        subtitleInfoText(wrapText = true) {
                            parseEnumValue<TranslationKey>(
                                notificationGeoObject.tags.getUniqueTag(
                                    TranslationTags.TitleTranslationKey,
                                ),
                            )?.getShortMessage(notificationGeoObject)
                                ?.renderText()
                        }
                    },
                    subtitleInfo = {
                        if (parseEnumValue<NotificationState>(notificationGeoObject.tags.getUniqueTag(NotificationTags.NotificationState)) == NotificationState.UNREAD) {
                            span("text-xs font-bold") {
                                translate(TL.Notifications.NEW)
                            }
                        }
                    },
                )
            }
        }
    }
}

//fun RenderContext.notificationFilterBar() {
//    val notificationFilterStore: NotificationFilterStore by koinCtx.inject()
//    val translation: Translation by koinCtx.inject()
//    val globalNotificationResultsStore: GlobalNotificationResultsStore by koinCtx.inject()
//
//    div(
//        {
//            width { full }
//            paddings {
//                top { tiny }
//            }
//        },
//    ) {
//        flexBox(
//            {
//                width { full }
//                direction { row }
//                justifyContent { spaceBetween }
//                alignItems { center }
//                border {
//                    color { primary.main }
//                    width(FormationDefault.formationStyles.borderWidth)
//                }
//                radius(FormationDefault.formationStyles.inputRadius)
//                background { color { secondary.main } }
//            },
//        ) {
//            notificationFilterStore.data.combine(
//                globalNotificationResultsStore.data.map {
//                    globalNotificationResultsStore.totalUnreadNotifications()
//                },
//            ) { filter, number -> Pair(filter, number) }.render { data ->
//                button(
//                    {
//                        if (data.first == NotificationFilter.NOTIFICATIONS) filterButtonOnStyle() else filterButtonOffStyle()
//                        fontSize { small }
//                    },
//                ) {
//                    +"${translation.getString(TL.Notifications.NOTIFICATIONS, mapOf("number" to 2))} ${if (data.second > 0) "(${data.second})" else ""}"
//
//                    clicks.map { NotificationFilter.NOTIFICATIONS } handledBy notificationFilterStore.selectFilter
//                }
//                // TODO change style, when button is used and remove tooltip
//                button(
//                    {
//                        if (data.first == NotificationFilter.MESSAGES) filterButtonOnStyle() else filterButtonDisabledStyle()
//                    },
//                ) {
//                    title(translation.getString(TL.General.COMING_SOON))
//                    +translation.getString(TL.Notifications.MESSAGES)
////                    clicks.map { NotificationFilter.MESSAGES } handledBy notificationFilterStore.selectFilter
//                }
//            }
//        }
//    }
//}

fun RenderContext.notificationResultsList(results: Flow<NotificationNumbers>) {

    val translation: Translation by koinCtx.inject()
    val notificationFilterStore: NotificationFilterStore by koinCtx.inject()
    val notificationSearchInputFieldStore: NotificationSearchInputFieldStore by koinCtx.inject()
    val objectAndUserHandler: ObjectAndUserHandler by koinCtx.inject()

    results.render { (reads, _, unreads, _) ->
        if ((unreads + reads).isNotEmpty()) {
            stackUp(
                {
                    width { full }
                    alignItems { center }
                },
                id = "list-entry-stack-up",
            ) {
                spacing { small }
                items {
                    unreads.forEach { result ->
                        div("w-full p-1 cursor-pointer hover:bg-gray-100") {
                            newNotificationListEntry(result)
                            clicks handledBy {
                                objectAndUserHandler.updateAndLocateObjectOrUserFromList(result)
                                notificationFilterStore.reset(Unit)
                                notificationSearchInputFieldStore.reset(Unit)
                            }
                        }
                    }
                    separationLine(
                        title = notificationFilterStore.data.map { filter ->
                            when (filter) {
                                NotificationFilter.NOTIFICATIONS -> translation.getString(TL.Notifications.OLDER_ALERTS)
                                NotificationFilter.MESSAGES -> translation.getString(TL.Notifications.OLDER_MESSAGES)
                            }
                        },
                    )
                    reads.forEach { result ->
                        div("w-full p-1 cursor-pointer hover:bg-gray-100") {
                            newNotificationListEntry(result)
                            clicks handledBy {
                                objectAndUserHandler.updateAndLocateObjectOrUserFromList(result)
                                notificationFilterStore.reset(Unit)
                                notificationSearchInputFieldStore.reset(Unit)
                            }
                        }
//                        newNotificationListEntry(
//                            result,
//                            clickHandlers = listOf(
//                                notificationFilterStore.reset,
//                                notificationSearchInputFieldStore.reset,
//                            ),
//                        )
                    }
                }
            }
        } else {
            flexBox(
                {
                    width { full }
                    height { full }
                    alignItems { center }
                    justifyContent { center }
                },
            ) {
                span {
                    when (notificationFilterStore.current) {
                        NotificationFilter.NOTIFICATIONS -> translation[TL.Search.NO_NOTIFICATIONS].renderText(into = this)
                        NotificationFilter.MESSAGES -> translation[TL.Search.NO_MESSAGES].renderText(into = this)
                    }
                }
            }
        }
    }
}

//fun RenderContext.notificationInfo(geoObject: GeoObjectDetails) {
//
//    val translation: Translation by koinCtx.inject()
//
//    lineUp {
//        spacing { tiny }
//        items {
//            span(
//                {
//                    fontSize { "9px" }
//                },
//            ) {
//                geoObject.createdAt.parseInstant()?.let { createdAtTime ->
//                    +createdAtTime.formatDateTime()
//                }
//            }
//            if (parseEnumValue<NotificationState>(geoObject.tags.getUniqueTag(NotificationTags.NotificationState)) == NotificationState.UNREAD) {
//                span(
//                    {
//                        fontSize { "9px" }
//                        fontWeight { bold }
//                    },
//                ) { translation[TL.Notifications.NEW].renderText(into = this) }
//            }
//        }
//
//    }
//}

//fun RenderContext.notificationListEntry(
//    notificationGeoObject: GeoObjectDetails,
//    clickHandlers: List<SimpleHandler<Unit>>? = null
//) {
//    val objectAndUserHandler: ObjectAndUserHandler by koinCtx.inject()
//    val hasConnectedObject = !notificationGeoObject.connectedTo.isNullOrEmpty()
//    val connectedObjectTitle = notificationGeoObject.tags.getUniqueTag(LocalizationArg.OBJECT_TITLE)
////    console.log(notificationGeoObject)
//    button(
//        {
//            width { full }
//            height { maxContent }
//            overflow { visible }
//        },
//        id = "list-entry-button",
//    ) {
//        if (hasConnectedObject) {
//            listEntryBox(
//                additionalStyle = {
//                    if (parseEnumValue<NotificationState>(notificationGeoObject.tags.getUniqueTag(NotificationTags.NotificationState)) == NotificationState.UNREAD) {
//                        border {
//                            color { primary.main }
//                            width(FormationDefault.formationStyles.borderWidth)
//                        }
//                    }
//                },
//            ) {
//                iconBox(notificationGeoObject, showStateIndicator = true)
//                flexBox(
//                    {
//                        width { full }
//                        height { full }
//                        direction { column }
//                        alignItems { start }
//                        justifyContent { start }
//                        overflow { hidden }
//                    },
//                ) {
//                    notificationInfo(notificationGeoObject)
//                    titleSubtitleStack {
//                        connectedObjectTitle?.let { title ->
//                            titleBox(
//                                titleText = {
//                                    titleSizedText { +title }
//                                },
//                            )
//                        }
//                        subtitleBox(
//                            subtitleText = {
//                                subtitleInfoText(wrapText = true) {
//                                    parseEnumValue<TranslationKey>(
//                                        notificationGeoObject.tags.getUniqueTag(
//                                            TranslationTags.TitleTranslationKey,
//                                        ),
//                                    )?.getShortMessage(notificationGeoObject)
//                                        ?.renderText()
//                                }
//                            },
//                        )
//                    }
//                }
//                icon { fromTheme { chevronRight } }
//            }
//        }
//        with(clicks) {
//            clickHandlers?.forEach { handler ->
//                this handledBy handler
//            }
//            this.map { notificationGeoObject } handledBy objectAndUserHandler.updateAndLocateObjectOrUserFromList
//
//        }
//    }
//}
